import regexEscape from 'rise-ui/utils/regex-escape.js';
import { string } from 'rise-ui/utils/string.js';

export const labelOrConfig = (labelConfig, config) => {
  const label = typeof labelConfig === 'string' ? labelConfig : '';
  config = Object.assign(!label && labelConfig || {}, config);
  return label ? { [label]: config } : config;
};

/**
 * Call with Route queryParam config to add `refreshModel: true`
 * @example
 * export default class extends Route {
 *   queryParams = {
 *     page: refreshes({ as: 'page' }), // Will refresh model
 *     other: { as: 'other' },          // Won't refresh model
 *   };
 * }
 * @param {string|{}} [config] queryParam config or label
 * @return {{}&{refreshModel: boolean}}
 */
export const refreshes = config =>
  labelOrConfig(config, { refreshModel: true });

/**
 * Call with Route queryParams config to add `refreshModel: true` to each
 * @example
 * export default class extends Route {
 *   queryParams = refresh(
 *     [ 'page', 'search', 'filter' ], // Will all refresh model
 *     { other: { as: 'other' } }      // Won't refresh model
 *   );
 * }
 * @param {{}|(string|{})[]} refreshParams Params to add `refreshModel: true` to
 * @param {{}} [queryParams] Additional unmodified queryParams to pass
 * @return {{}} Modified queryParams config
 */
export const refresh = (refreshParams, queryParams) => Object.assign(
  [].concat(queryParams || {}).reduce((qps, qp) => {
    if (typeof qp === 'string') {
      qps[qp] = labelOrConfig(qp);
    } else {
      Object.assign(qps, qp || {});
    }
    return qps;
  }, {}),
  [].concat(refreshParams).reduce((qps, qp) => {
    if (typeof qp === 'string') {
      qps[qp] = refreshes();
    } else {
      Object.keys(qp).forEach(iqp => {
        qps[iqp] = refreshes(qp[iqp]);
      });
    }
    return qps;
  }, {})
);

/**
 * Call with Route queryParams config to expand simple arrays to expected object form.
 * @example
 * export default class extends Route {
 *   queryParams = simple([
 *     'page', 'search', 'filter', // Will be converted to expected form.
 *     { other: { as: 'other' } }  // Intersperse full format between simple.
 *     'tab',                      // Another simple.
 *   ]);
 * }
 * @param {{}|(string|{})[]} params Query-params to transform/pass
 * @return {{}} Modified queryParams config
 */
export const simple = (params) => [].concat(params || {}).reduce((qps, qp) => {
  if (typeof qp === 'string') {
    qps[qp] = labelOrConfig(qp);
  } else if (qp) {
    Object.assign(qps, qp);
  }
  return qps;
}, {});

export const encodingCharacters = {
  escape: '_',
  array:  '-',
  object: '.',
};

export const parseChars = chars => {
  chars = Object.assign({}, encodingCharacters, chars || {});
  Object.keys(chars).forEach(key => chars[key] = string(chars[key]));
  return chars;
};

/**
 * Escapes encoding characters in a string
 * @param {string} val
 * @param chars
 * @return {*}
 */
export const escape = (val, chars) => {
  chars = parseChars(chars);
  if (!val || !chars.escape) {
    return val;
  }
  const matchChars = Object.keys(chars).reduce((cs, c) => {
    cs += `\\${chars[c]}`;
    return cs;
  }, '');
  return val.replace(
    new RegExp(`([${matchChars}])`, 'g'),
    `${chars.escape}$1`
  );
};

/**
 * Encode object/array to queryParam
 * Works with object inside array or array inside object, but no more than that.
 * @param {{}|[]|*} obj Object/array to encode
 * @param {{escape:string,array:string,object:string}} [chars] Encoding characters to use
 * @return {string}
 */
export const encode = (obj, chars) => {
  chars = parseChars(chars);
  const esc = v => escape(v, chars);
  if (typeof obj !== 'object' || !obj) {
    return obj === 0 ? '0' : !obj ? '' : esc('' + obj);
  }
  const isArray = Array.isArray(obj);
  let result = '';
  if (isArray) {
    result = obj.map(v => encode(v, chars)).join(chars.array);
  } else {
    result = Object.keys(obj)
      .reduce((parts, key) => [ ...parts, esc(key), encode(obj[key], chars) ], [])
      .join(chars.object);
  }
  return result || undefined;
};

/**
 * Encode each property of object
 */
export const encodeObject = (obj, chars) => {
  const result = {};
  if (typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      result[key] = encode(obj[key], chars);
    });
  }
  return result;
};

/**
 * Encode each item of array
 */
export const encodeArray = (arr, chars) => {
  return arr && arr.map(item => encode(item, chars));
};

/**
 * Index of next unescaped occurrence of named encode character
 * @param {'escape'|'array'|'object'} char Named encode character to look for
 * @param {{escape:string,array:string,object:string}} [chars] Encoding characters to use
 * @return {function(string): number}
 */
export const getIndexOfEncodeChar = (char, chars) => {
  chars = parseChars(chars);
  char = chars[char];
  const escChar = char ? regexEscape(char) : '';
  let escape = chars.escape ? regexEscape(chars.escape) : '';
  escape = escape ? `(^|(^|[^${escape}${escChar}])(${escape}{2})*)` : '';
  const matcher = new RegExp(`${escape}${escChar}`);
  return str => {
    let index = str.search(matcher);
    if (index >= 0) {
      index = str.indexOf(char, index);
    }
    return index;
  };
};

/**
 * Decode an object/array encoded into a queryParam
 * @param {string} str Query param to decode to object/array
 * @param {{escape:string,array:string,object:string}} [chars] Encoding characters to use
 * @return {{}|[]|*|null}
 */
export const decode = (str, chars) => {
  if (!str || typeof str !== 'string') {
    return str || null;
  }
  chars = parseChars(chars);
  let result;
  const matchEscaped = chars.escape ? new RegExp(`${chars.escape}(.)`, 'g') : null;
  const stripped = chars.escape ? str.replace(matchEscaped, '') : str;
  const getIndexOfArray = getIndexOfEncodeChar('array', chars);
  const getIndexOfObject = getIndexOfEncodeChar('object', chars);
  const indexOfArray = getIndexOfArray(stripped);
  const indexOfObject = getIndexOfObject(stripped);
  if (indexOfArray >= 0
    && ( indexOfObject < 0 || indexOfArray < indexOfObject )
  ) {
    result = [];
    while (str) {
      let index = getIndexOfArray(str);
      if (index < 0) {
        index = str.length;
      }
      result.push(decode(str.substring(0, index), chars));
      str = str.substr(index + 1);
    }
  } else if (indexOfObject >= 0
    // && ( indexOfArray < 0 || indexOfObject < indexOfArray ) // Implied by above
  ) {
    result = {};
    let lastKey;
    const couple = keyVal => {
      if (!lastKey) {
        lastKey = keyVal;
      } else {
        result[lastKey] = decode(keyVal, chars);
        lastKey = null;
      }
      return keyVal;
    };
    while (str) {
      let index = getIndexOfObject(str);
      if (index < 0) {
        index = str.length;
      }
      couple(str.substring(0, index));
      str = str.substr(index + 1);
    }
  } else {
    result = str.replace(matchEscaped, '$1');
  }
  return result;
};

/**
 * Call with Route queryParam config to add `complex: true`
 * @example
 * export default class extends Route {
 *   queryParams = {
 *     page: complex({ as: 'page' }), // Adds `complex: true`
 *     other: { as: 'other' },
 *   };
 * }
 * @param {string|{}} [config] queryParam config
 * @return {{}&{complex: boolean}}
 */
export const complex = (config) =>
  labelOrConfig(config, { complex: true });

const filterEmpties = value => value === 0 || value;

/**
 * Strips falsy values from given array or object.
 * @param {[]|{}} obj Array or object to strip empty values from
 * @param {[]} [except] Keys to skip
 * @return {[]|{}|undefined}
 */
export const stripEmpties = (obj, except) => {
  let result;
  except = except && [].concat(except);
  const filterWithExceptions = (value, key) =>
    except && except.indexOf(key) >= 0 || filterEmpties(value);
  if (Array.isArray(obj)) {
    result = obj.filter(filterWithExceptions);
  } else {
    result = {};
    Object.keys(obj).forEach(key => {
      if (filterWithExceptions(obj[key], key)) {
        result[key] = obj[key];
      }
    });
  }
  return result;
};
